import type { ChatColleague, ChatConversation, ChatMessage } from "@/lib/chatApi";
import { conversationListPreview } from "@/lib/chatFormat";
import { messagePoll } from "@/lib/chatPolls";

export type ChatLastInteract = {
  kind: "vote" | "reaction" | string;
  user_id: number;
  emoji?: string | null;
};

export type ChatConversationSidebar = ChatConversation & {
  last_activity_label?: string;
  last_interact?: ChatLastInteract | null;
};

export function chatActorFirstName(
  userId: number,
  selfId: number,
  conv: ChatConversation,
  colleagues: ChatColleague[] = [],
  fallback?: string | null
): string {
  if (userId === selfId) return "Vous";
  const mem = conv.members?.find((m) => m.id === userId);
  const col = colleagues.find((c) => c.id === userId);
  const raw =
    mem?.display_name?.trim() ||
    mem?.username?.trim() ||
    col?.display_name?.trim() ||
    col?.username?.trim() ||
    fallback?.trim() ||
    "";
  if (!raw) return "Quelqu’un";
  const first = raw.split(/\s+/)[0];
  return first || raw;
}

function lastVoterId(last: ChatMessage | null | undefined): number | null {
  const poll = messagePoll(last);
  const id = poll?.last_voter_id;
  return typeof id === "number" && id > 0 ? id : null;
}

export function conversationSidebarPreview(
  c: ChatConversationSidebar,
  selfId: number,
  colleagues: ChatColleague[] = []
): { text: string; actor: string | null; isVote: boolean } {
  const hint = c.last_activity_label?.trim();
  if (hint) {
    return { text: hint, actor: null, isVote: true };
  }

  const interact = c.last_interact;
  if (interact && interact.user_id > 0) {
    const name = chatActorFirstName(interact.user_id, selfId, c, colleagues);
    if (interact.kind === "reaction") {
      const emoji = (interact.emoji || "").trim();
      return {
        text: name === "Vous" ? `Vous avez réagi${emoji ? ` ${emoji}` : ""}` : `${name} a réagi${emoji ? ` ${emoji}` : ""}`,
        actor: name === "Vous" ? null : name,
        isVote: false,
      };
    }
    return {
      text: name === "Vous" ? "Vous avez voté" : `${name} a voté`,
      actor: name === "Vous" ? null : name,
      isVote: true,
    };
  }

  const last = c.last_message ?? null;
  const voterId = lastVoterId(last);
  if (voterId != null) {
    const name = chatActorFirstName(voterId, selfId, c, colleagues);
    return {
      text: name === "Vous" ? "Vous avez voté" : `${name} a voté`,
      actor: name === "Vous" ? null : name,
      isVote: true,
    };
  }

  const base = conversationListPreview(c);
  if (!last) return { text: base, actor: null, isVote: false };
  const name = chatActorFirstName(last.sender_id, selfId, c, colleagues, last.sender_name);
  const actor = last.sender_id === selfId ? null : name;
  if (c.type === "group") {
    const prefix = last.sender_id === selfId ? "Vous" : name;
    return { text: `${prefix} : ${base}`, actor, isVote: false };
  }
  return { text: base, actor, isVote: false };
}
